/**
 * Reads the TUNABLES block out of a game.
 *
 * The Tweak panel needs to offer a child sliders for "how high you jump" without
 * anyone hand-maintaining a mapping per game — the games are generated, so the
 * mapping has to be derived from the source. The build prompt asks the model to
 * declare tunables as `const NAME = value; // plain language label`, and this
 * reads exactly that shape back out.
 *
 * A game that does not follow the convention simply yields no tunables and the
 * panel hides itself. This is a nice-to-have surface, never a required one.
 */

export type Tunable = {
  name: string;
  value: number;
  /** The trailing comment — this is what the child actually reads. */
  label: string;
  /** Slider bounds, derived from the declared value. */
  min: number;
  max: number;
  step: number;
  line: number;
};

const DECL = /^[ \t]*const[ \t]+([A-Z][A-Z0-9_]{2,})[ \t]*=[ \t]*(-?\d+(?:\.\d+)?)[ \t]*;[ \t]*\/\/[ \t]*(.+?)[ \t]*$/gm;

/**
 * Bounds are derived rather than declared, because the model cannot be relied on
 * to declare sensible ones. Range is generous enough to make a visible
 * difference and tight enough that a child cannot make the game unplayable.
 */
/**
 * Round to a fixed number of SIGNIFICANT figures rather than decimal places.
 *
 * A fixed two decimal places collapses a small tunable — a per-frame speed-up of
 * 0.0009, say — to a min and max of both zero, producing a slider that cannot
 * move. The failure is silent: the panel renders, the child drags, nothing
 * happens.
 */
const toSignificant = (value: number, figures = 3): number => {
  if (value === 0) return 0;
  const digits = Math.min(12, Math.max(0, figures - 1 - Math.floor(Math.log10(Math.abs(value)))));
  return Number(value.toFixed(digits));
};

const boundsFor = (value: number): { min: number; max: number; step: number } => {
  if (value === 0) return { min: 0, max: 10, step: 1 };
  const magnitude = Math.abs(value);
  const isInteger = Number.isInteger(value);
  const min = isInteger ? Math.max(0, Math.floor(magnitude * 0.25)) : toSignificant(magnitude * 0.25);
  const max = isInteger ? Math.ceil(magnitude * 2.5) : toSignificant(magnitude * 2.5);
  const step = isInteger ? 1 : toSignificant(magnitude / 20);
  return value < 0 ? { min: -max, max: -min, step } : { min, max, step };
};

export function parseTunables(html: string): Tunable[] {
  const found: Tunable[] = [];
  const seen = new Set<string>();
  DECL.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DECL.exec(html)) !== null) {
    const [, name, raw, label] = m as unknown as [string, string, string, string];
    if (seen.has(name)) continue;
    seen.add(name);
    const value = Number(raw);
    if (!Number.isFinite(value)) continue;
    found.push({
      name,
      value,
      label,
      ...boundsFor(value),
      line: html.slice(0, m.index).split('\n').length,
    });
  }
  return found;
}

/**
 * Apply an edited value back into the source. Rewrites only the declaration
 * line, and only when the name still matches — so a stale panel can never
 * corrupt a game.
 */
export function applyTunable(html: string, name: string, value: number): string {
  if (!/^[A-Z][A-Z0-9_]{2,}$/.test(name) || !Number.isFinite(value)) return html;
  const line = new RegExp(
    `^([ \\t]*const[ \\t]+${name}[ \\t]*=[ \\t]*)-?\\d+(?:\\.\\d+)?([ \\t]*;.*)$`,
    'm',
  );
  return html.replace(line, `$1${value}$2`);
}

export type TunableChange = { name: string; value: number };

export type ApplyResult = {
  html: string;
  applied: TunableChange[];
  rejected: { name: string; reason: 'unknown' | 'out_of_range' | 'not_a_number' }[];
};

/**
 * Apply a set of slider changes to a game.
 *
 * Runs on the SERVER against the stored bundle, and takes only name/value pairs
 * from the client — never client-supplied HTML. A child (or anything pretending
 * to be one) can therefore only ever move a number that already exists in their
 * own game, within bounds this file derived. There is no path from the Tweak
 * panel to arbitrary code in a bundle.
 *
 * Unknown names and out-of-range values are reported rather than silently
 * dropped, so the panel can say why a change did not take instead of appearing
 * to work and not working.
 */
export function applyTunableChanges(html: string, changes: TunableChange[]): ApplyResult {
  const available = new Map(parseTunables(html).map((t) => [t.name, t]));
  const applied: TunableChange[] = [];
  const rejected: ApplyResult['rejected'] = [];
  let next = html;

  for (const change of changes) {
    const tunable = available.get(change.name);
    if (!tunable) {
      rejected.push({ name: change.name, reason: 'unknown' });
      continue;
    }
    if (typeof change.value !== 'number' || !Number.isFinite(change.value)) {
      rejected.push({ name: change.name, reason: 'not_a_number' });
      continue;
    }
    if (change.value < tunable.min || change.value > tunable.max) {
      rejected.push({ name: change.name, reason: 'out_of_range' });
      continue;
    }

    const updated = applyTunable(next, change.name, change.value);
    if (updated === next) {
      // The declaration did not match on rewrite; treat as unknown rather than
      // reporting a success that changed nothing.
      rejected.push({ name: change.name, reason: 'unknown' });
      continue;
    }
    next = updated;
    applied.push(change);
  }

  return { html: next, applied, rejected };
}
